import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Card,
    CardActionArea,
    Box,
    Typography,
    Tabs,
    Tab,
    Pagination,
    InputAdornment,
} from '@mui/material';
import Iconify from '../iconify';
import SvgColor from '../svg-color';
import { ICON_CATEGORIES, searchIcons, getIconPath } from './iconLibrary';

// ----------------------------------------------------------------------

const ICONS_PER_PAGE = 24;

export default function IconSelectorModal({ open, onClose, onSelect, selectedIcon }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [category, setCategory] = useState('all');
    const [page, setPage] = useState(1);

    // Filter and paginate icons
    const filteredIcons = useMemo(
        () => searchIcons(searchQuery, category),
        [searchQuery, category]
    );

    const totalPages = Math.ceil(filteredIcons.length / ICONS_PER_PAGE);
    const paginatedIcons = filteredIcons.slice(
        (page - 1) * ICONS_PER_PAGE,
        page * ICONS_PER_PAGE
    );

    const handleCategoryChange = (event, newValue) => {
        setCategory(newValue);
        setPage(1);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setPage(1);
    };

    const handleIconSelect = (iconValue) => {
        onSelect(iconValue);
        onClose();
    };

    const handleClose = () => {
        setSearchQuery('');
        setCategory('all');
        setPage(1);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Typography variant="h6">Select Icon</Typography>
            </DialogTitle>

            <DialogContent>
                {/* Search Field */}
                <TextField
                    fullWidth
                    placeholder="Search icons..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    sx={{ mb: 3 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Iconify icon="eva:search-fill" />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Category Tabs */}
                <Tabs
                    value={category}
                    onChange={handleCategoryChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                >
                    {ICON_CATEGORIES.map((cat) => (
                        <Tab key={cat.value} label={cat.label} value={cat.value} />
                    ))}
                </Tabs>

                {/* Icons Grid */}
                <Grid container spacing={2} sx={{ minHeight: 400 }}>
                    {paginatedIcons.map((icon) => (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={icon.value}>
                            <Card
                                sx={{
                                    border: (theme) =>
                                        selectedIcon === icon.value
                                            ? `2px solid ${theme.palette.primary.main}`
                                            : '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: selectedIcon === icon.value ? 'action.selected' : 'background.paper',
                                }}
                            >
                                <CardActionArea
                                    onClick={() => handleIconSelect(icon.value)}
                                    sx={{
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    {/* Render navbar SVG icons or Iconify icons */}
                                    {icon.type === 'navbar' ? (
                                        <SvgColor
                                            src={getIconPath(icon.value)}
                                            sx={{ width: 32, height: 32 }}
                                        />
                                    ) : (
                                        <Iconify icon={icon.value} width={32} />
                                    )}
                                    <Typography variant="caption" align="center" noWrap sx={{ width: '100%' }}>
                                        {icon.label}
                                    </Typography>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* No Results */}
                {filteredIcons.length === 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 400,
                            gap: 2,
                        }}
                    >
                        <Iconify icon="eva:search-outline" width={64} sx={{ color: 'text.disabled' }} />
                        <Typography variant="body2" color="text.secondary">
                            No icons found
                        </Typography>
                    </Box>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} color="inherit">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}

IconSelectorModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    selectedIcon: PropTypes.string,
};
